import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, Percent, Store, Rocket } from "lucide-react";

export default function BecomeSeller() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [form, setForm] = useState({
    business_name: "", business_type: "individual", pan: "", gstin: "",
    phone: "", address_line: "", city: "", state: "", pincode: "",
    bank_account: "", ifsc: "", account_holder: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/vendor/me").then(({ data }) => {
      if (data.status !== "not_applied") {
        setVendor(data);
        setForm({ ...form, ...data });
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  if (!user) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <h1 className="font-display text-3xl text-[#0F3D2E]">Sign in to become a seller</h1>
      <Link to="/register?role=vendor"><Button className="mt-6 rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white">Sign Up</Button></Link>
    </div>
  );

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/vendor/apply", form);
      setVendor(data);
      await refreshMe();
      toast.success("Application submitted for review");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSaving(false); }
  };

  const statusUi = () => {
    if (!vendor) return null;
    const statusMap = {
      pending: { icon: Clock, color: "bg-amber-100 text-amber-700", text: "Under Review" },
      approved: { icon: CheckCircle2, color: "bg-green-100 text-green-700", text: "Approved" },
      rejected: { icon: XCircle, color: "bg-red-100 text-red-700", text: "Rejected" },
      suspended: { icon: XCircle, color: "bg-red-100 text-red-700", text: "Suspended" },
    };
    const s = statusMap[vendor.status] || statusMap.pending;
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${s.color}`} data-testid="vendor-status">
        <s.icon size={16} /> {s.text}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">For businesses</div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0F3D2E]">Sell on Florvanta</h1>
      <p className="mt-3 text-[#3B5249] max-w-2xl">Reach customers across India. We take only 1% commission — you keep 99% of every sale. No listing or monthly fees.</p>

      <div className="mt-6 grid md:grid-cols-3 gap-3 text-sm">
        {[
          [Percent, "1% Commission", "Lowest in India"],
          [Store, "Unlimited Listings", "Zero listing fees"],
          [Rocket, "Fast Onboarding", "Go live in 24hrs after KYC"],
        ].map(([Icon, t, d], i) => (
          <div key={i} className="rounded-xl border border-[#E2E8E5] bg-white p-4 flex gap-3">
            <Icon size={22} className="text-[#14A085] shrink-0" />
            <div>
              <div className="font-medium text-[#0F3D2E]">{t}</div>
              <div className="text-xs text-[#6E877C]">{d}</div>
            </div>
          </div>
        ))}
      </div>

      {vendor && (
        <div className="mt-8 rounded-xl border border-[#E2E8E5] bg-white p-6" data-testid="vendor-status-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Application Status</div>
              <div className="mt-1 font-display text-2xl text-[#0F3D2E]">{vendor.business_name}</div>
            </div>
            {statusUi()}
          </div>
          {vendor.status === "approved" && (
            <div className="mt-4">
              <Button onClick={() => nav("/vendor")} className="rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white" data-testid="go-to-dashboard">
                Go to Vendor Dashboard →
              </Button>
            </div>
          )}
          {vendor.note && (
            <p className="mt-3 text-sm text-[#3B5249]">Note from admin: {vendor.note}</p>
          )}
        </div>
      )}

      <form onSubmit={submit} className="mt-8 rounded-xl border border-[#E2E8E5] bg-white p-6 space-y-6" data-testid="vendor-apply-form">
        <div>
          <h2 className="font-display text-xl text-[#0F3D2E]">Business Details</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <Field label="Business Name" required v={form.business_name} on={upd("business_name")} tid="v-name" />
            <div>
              <label className="text-xs text-[#3B5249]">Business Type</label>
              <select className="mt-1 w-full h-10 rounded-md border border-[#E2E8E5] bg-white px-3 text-sm" value={form.business_type} onChange={upd("business_type")} data-testid="v-type">
                <option value="individual">Individual</option>
                <option value="proprietorship">Proprietorship</option>
                <option value="private_limited">Private Limited</option>
                <option value="llp">LLP</option>
              </select>
            </div>
            <Field label="PAN Number" required v={form.pan} on={upd("pan")} tid="v-pan" />
            <Field label="GSTIN (optional)" v={form.gstin} on={upd("gstin")} tid="v-gstin" />
            <Field label="Contact Phone" required v={form.phone} on={upd("phone")} tid="v-phone" />
          </div>
        </div>

        <div>
          <h2 className="font-display text-xl text-[#0F3D2E]">Pickup Address</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <Field label="Address Line" required v={form.address_line} on={upd("address_line")} tid="v-address" full />
            <Field label="City" required v={form.city} on={upd("city")} tid="v-city" />
            <Field label="State" required v={form.state} on={upd("state")} tid="v-state" />
            <Field label="Pincode" required v={form.pincode} on={upd("pincode")} tid="v-pincode" />
          </div>
        </div>

        <div>
          <h2 className="font-display text-xl text-[#0F3D2E]">Bank Details (for settlements)</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <Field label="Account Holder Name" required v={form.account_holder} on={upd("account_holder")} tid="v-holder" />
            <Field label="Account Number" required v={form.bank_account} on={upd("bank_account")} tid="v-account" />
            <Field label="IFSC Code" required v={form.ifsc} on={upd("ifsc")} tid="v-ifsc" />
          </div>
        </div>

        <Button type="submit" disabled={saving} className="rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white px-8" data-testid="vendor-submit">
          {saving ? "Submitting…" : vendor ? "Update Application" : "Submit for Review"}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, required, v, on, tid, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs text-[#3B5249]">{label}{required && " *"}</label>
      <Input required={required} value={v} onChange={on} className="mt-1" data-testid={tid} />
    </div>
  );
}
